"use client";

import React, { useState } from "react";
import Link from "next/link";
import {
  FAQ_COPY,
  LISTING_FAQ_CATEGORY,
  LISTING_FAQ_CATEGORY_COUNT,
} from "@/src/features/faq/content";
import type { TFaqCategoryId, TFaqLang } from "@/src/features/faq/types";
import { faqService } from "@/src/features/faq/services";
import { useFaqReveal } from "@/src/features/faq/hooks/useFaqReveal";
import FaqCategoryGroup from "@/src/features/faq/components/FaqCategoryGroup";
import FaqLangToggle from "@/src/features/faq/components/FaqLangToggle";

const LISTING_FAQ_GROUPS = faqService.getTeaserGroups(
  LISTING_FAQ_CATEGORY,
  LISTING_FAQ_CATEGORY_COUNT,
);

/**
 * Inline FAQ for the listing / hotel details page — four categories led by the
 * property-related one, shown after every other section and above the footer,
 * with the rest on `/faq`. Every category starts collapsed so the block stays a
 * short list at the end of an already long page; expanding one shows all of
 * that category's questions.
 *
 * Outline: h2 (section) → h3 (category) → h4 (questions). No `FAQPage`
 * structured data here; `/faq` is the single canonical source for that.
 */
const ListingFaqSection: React.FC = () => {
  const [lang, setLang] = useState<TFaqLang>("en");
  /** Categories the reader has expanded; none to begin with. */
  const [openGroups, setOpenGroups] = useState<TFaqCategoryId[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  // Newly revealed questions need observing, so reset the reveal on expand too.
  const revealRef = useFaqReveal<HTMLDivElement>(
    `${lang}|${openGroups.join(",")}`,
  );

  if (LISTING_FAQ_GROUPS.length === 0) return null;

  const copy = FAQ_COPY[lang];

  const toggleGroup = (id: TFaqCategoryId) =>
    setOpenGroups((prev) =>
      prev.includes(id) ? prev.filter((catId) => catId !== id) : [...prev, id],
    );

  return (
    <section
      aria-labelledby="listing-faq-title"
      className="font-sans text-ink-black"
    >
      <div className="flex items-start justify-between gap-3 md:items-end md:gap-[30px]">
        <div>
          {/* Same size / weight as the "Facilities" h2 on this page. */}
          <h2
            id="listing-faq-title"
            className="m-0 text-xl font-bold text-ink-black [text-wrap:pretty] md:text-2xl"
          >
            {copy.listingFaqTitle}
          </h2>
          <p className="mt-2 text-13 leading-[1.6] text-ink-secondary md:text-14">
            {copy.listingFaqSub}
          </p>
        </div>
        <FaqLangToggle lang={lang} onChange={setLang} size="sm" />
      </div>

      <div
        ref={revealRef}
        className="mt-[18px] flex flex-col gap-4 md:mt-[22px]"
      >
        {LISTING_FAQ_GROUPS.map((group) => (
          <FaqCategoryGroup
            key={group.id}
            group={group}
            lang={lang}
            surface="listing"
            open={openGroups.includes(group.id)}
            onToggle={() => toggleGroup(group.id)}
            openItemId={openId}
            onToggleItem={(itemId) =>
              setOpenId(openId === itemId ? null : itemId)
            }
          />
        ))}
      </div>

      <Link
        href="/faq"
        className="mt-[18px] flex items-center justify-center gap-2.5 rounded-full border border-neutral-300 px-6 py-3.5 text-14 font-semibold text-ink-black transition-colors duration-300 hover:border-brand-500 hover:text-brand-500 md:mt-[22px] md:inline-flex"
      >
        {copy.seeMore}
        <span aria-hidden="true" className="text-16 leading-none">
          →
        </span>
      </Link>
    </section>
  );
};

export default ListingFaqSection;
